import * as React from "react";
import { Button, HtmlEditor, SelectBox, TagBox, TextBox, ValidationGroup, ValidationSummary, Validator } from "devextreme-react";
import { Item, Toolbar } from "devextreme-react/html-editor";
import { ValueChangedInfo } from "devextreme/ui/editor/editor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { IApiUrls } from "../../models/IApiUrls";
import { DefaultGoedePraktijk, IGoedePraktijk, IGoedePraktijkData, IGoedePraktijkDocument, IGoedePraktijkOperationeleDoelstelling, IGoedePraktijkThema } from "../../models/IGoedePraktijk";
import useDataSource, { useFetchData, useListSource, useToken } from "../../components/hooks/DataSource";
import DirtyPrompt from "../../components/DirtyPrompt";
import FileBox from "../../components/FileBox";
import { IDocument } from "../../models/IDocument";
import { IPageLoading } from "../../App";
import { RequiredRule } from "devextreme-react/validator";
import useAppRoutes from "../../AppRoutes";
import useNotification from "../../components/hooks/Notification";
import { IListsModel } from "../../models/IHomeModel";

interface IProps extends IPageLoading {
    apiUrls: IApiUrls,
    lists: IListsModel

}

interface IUrlParams {
    id: string;
}

const GoedePraktijk = ({ apiUrls, setPageLoading, lists }: IProps) => {
    const params = useParams<IUrlParams>();
    const fetcher = useFetchData();
    const history = useHistory();
    const routes = useAppRoutes();
    const notify = useNotification();
    //const fetchData = useFetchData();
    const [data, setData] = useState<IGoedePraktijk>(DefaultGoedePraktijk());
    const [dbOpDoel, setDbOpDoel] = useState<IGoedePraktijkOperationeleDoelstelling[]>([]);
    const [dbThemas, setDbThemas] = useState<IGoedePraktijkThema[]>([]);
    const [dbDocs, setDbDocs] = useState<IGoedePraktijkDocument[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const dsThema = useListSource(lists.themas, "naam"); // useUrlListSource(apiUrls.themas, "naam");
    const opDoelFilter = useMemo(() => ["!", ["hoofdDoelstellingId", "=", null]], []);
    const dsOpDoel = useListSource(lists.doelstellingen, "naam", opDoelFilter); // useUrlListSource(apiUrls.operationeleDoelstellingen, "naam", opDoelFilter);
    const dsVisRp = useDataSource(apiUrls.visitatieRapporten, "naam");
    const valGroup = useRef<ValidationGroup>(null);

    const token = useToken();

    const htmlOptions = useMemo(() => ({
        sizeValues: ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'],
        fontValues: ['Arial', 'Calibri', 'Courier New', 'Georgia', 'Impact', 'Lucida Console', 'Tahoma', 'Times New Roman', 'Verdana' ],  //, 'Flanders Art Sans', 'Flanders Art Serif'],
        headerValues: [false, 1, 2, 3, 4, 5],
        headerValuesRedacteur: [false, 3, 4],
        mediaResizing: { enabled: true },
        tableResizing: { enabled: true },
        tableContextMenu: { enabled: true },

    }), []);

    const titelChangedHandler = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, titel: e.value })), [])
    const opDoelChangedHandler = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, operationeleDoelstellingen: e.value })), [])
    const themaChangedHandler = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, themas: e.value })), [])
    const visRpChangedHandler = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, visitatieRapportId: e.value })), [])
    const omschrChangedHandler = useCallback((e: ValueChangedInfo) => setData(p => ({ ...p, htmlOmschrijving: e.value })), [])

    const docChangedHandler = useCallback((doc?: IDocument[]) => {
        setData(prev => ({ ...prev, documenten: doc || [] }))
    }, [])

    const createView = useCallback((apidata: IGoedePraktijkData) => {
        //keep gemeenten data (so we can send existing ids back to server)
        setDbOpDoel(apidata.operationeleDoelstellingen);
        setDbThemas(apidata.themas);
        setDbDocs(apidata.documenten);
        //Convert gemeenten
        setData({
            ...apidata,
            operationeleDoelstellingen: apidata.operationeleDoelstellingen.map(od => od.operationeleDoelstellingId),
            themas: apidata.themas.map(t => t.themaId),
            documenten: apidata.documenten.map(d => d.document!)
        });
        setIsDirty(false); //(re)loaded
        setPageLoading(false)
    }, [setPageLoading])


    //Set dirty flag on data changes
    useEffect(() => {
        //also executed on load! Will reset in next effect (data loading)
        data && setIsDirty(true);
    }, [data])

    //load data. (only runs when url id changes)
    useEffect(() => {
        //Reset data!
        setIsDirty(false); //dirty flag false when loading with id 0
        if (+params.id !== data.id) {
            setPageLoading(true);
            setDbOpDoel([]); 
            setDbThemas([]);
            setDbDocs([]);
            +params.id > 0 && fetcher(`${apiUrls.goedePraktijken}/${params.id}`).then(createView);
        }
    }, [fetcher, params.id, createView, apiUrls.goedePraktijken, setPageLoading, data.id])

    //save data
    const sendData = useCallback((publish: boolean = false) => {
        const isValid = valGroup.current!.instance.validate().isValid;
        if (!isValid) {
            notify("warning", "Er zijn validatiefouten!");
            return;
        }

        setPageLoading(true);

        const operationeleDoelstellingen = data.operationeleDoelstellingen
            .map(id => dbOpDoel.find(p => p.operationeleDoelstellingId === id) || { id: 0, operationeleDoelstellingId: id });

        const themas = data.themas.map(id => dbThemas.find(p => p.themaId === id) || { id: 0, themaId: id });
        const documenten = data.documenten.map(doc => {
            const result = dbDocs.find(p => p.documentId === doc.id) || { id: 0, documentId: doc.id }
            return { id: result.id, documentId: result.documentId }
        });

        const newData: IGoedePraktijkData = {
            ...data,
            operationeleDoelstellingen,
            themas,
            documenten,
            gepubliceerd: data.gepubliceerd || publish
        };

        console.log("send data", newData);

        fetcher(apiUrls.goedePraktijken + (data.id !== 0 ? '/' + data.id : ''), newData, data.id === 0 ? 'POST' : 'PUT').then((resp: IGoedePraktijkData) => {
            console.log("response", resp);
            createView(resp);
            data.id !== resp.id && history.replace(generatePath(routes.goedePraktijkenEdit, { id: resp.id }))
            notify("success", "Opslaan geslaagd!");
        }).catch(() => {
            setPageLoading(false);
            notify("error", "Opslaan mislukt!")
        });

    }, [createView, data, dbOpDoel, dbThemas, fetcher, apiUrls.goedePraktijken, dbDocs, setPageLoading, history, routes.goedePraktijkenEdit, notify])
    
    const onSaveHandler = useCallback(() => sendData(), [sendData])

    const onPublishHandler = useCallback(() => sendData(true), [sendData])

    const allowedFileExtensions = useMemo(() => ([".pdf"]), [])

    return <ValidationGroup ref={valGroup}>
        <DirtyPrompt when={isDirty} />
        <h2>{isDirty ? "*" : ""}Goede praktijk: {data.titel}</h2>

        
        <div className="form-group">
            <label className="">Titel</label>
            <TextBox
                value={data.titel}
                onValueChanged={titelChangedHandler}
            >
                <Validator>
                    <RequiredRule message="Titel is verplicht." />
                </Validator>
            </TextBox>
        </div>

        <div className="form-group">
            <label className="">Operationele doelstellingen</label>
            <TagBox
                dataSource={dsOpDoel}
                value={data.operationeleDoelstellingen}
                onValueChanged={opDoelChangedHandler}
                //hideSelectedItems
                showSelectionControls
                //hint="geef één of meerdere gemeenten op."                
                displayExpr="naam"
                valueExpr="id"
                //searchExpr={["naam", "postcode"]}
                searchEnabled
            >
                <Validator>
                    <RequiredRule message="Geef minstens één operationele doelstelling op." />
                </Validator>
            </TagBox>
        </div>

        <div className="form-group">

            <label className="">Thema</label>
            <TagBox
                dataSource={dsThema}
                value={data.themas}
                onValueChanged={themaChangedHandler}
                //hideSelectedItems
                showSelectionControls
                //hint="geef één of meerdere gemeenten op."                
                displayExpr="naam"
                valueExpr="id"
                //searchExpr={["naam", "postcode"]}
                searchEnabled
            />
        </div>

        <div className="form-group">

            <label>Visitatierapport</label>
            <SelectBox
                dataSource={dsVisRp}
                //displayExpr={shmFormatNaam}
                //displayExpr={"naam"}
                valueExpr="id"
                displayExpr="naam"
                searchEnabled
                searchMode="contains"
                //minSearchLength={2}
                //searchExpr={['nummer', 'naam']}
                value={data.visitatieRapportId}
                onValueChanged={visRpChangedHandler}
            >
                <Validator>
                    <RequiredRule message="Visitatierapport is verplicht." />
                </Validator>
            </SelectBox>
        </div>

        <div className="form-group">
            <label>Document(en)</label>

            <FileBox<IDocument>
                apiUrl={apiUrls.documenten}
                value={data.documenten}
                token={token}
                //allowedExtensions={allowedFileExtensions}
                valueChange={docChangedHandler}
                placeholder="(optioneel) PDF-documenten..."
                allowedExtensions={allowedFileExtensions}
                //inputAttr={inputAttr.doc}
                //required
                //requiredMessage="Visitatierapport is verplicht"
                multiple
            />

            {/*<FileUploader*/}
            {/*    name="file"*/}
            {/*    selectButtonText="Selecteer visitatierapport"*/}
            {/*    //labelText="test.pdf"*/}
            {/*    accept="application/pdf"*/}
            {/*    uploadMode="instantly"*/}
            {/*    allowCanceling*/}
            {/*    showFileList*/}
            {/*    uploadUrl={apiUrls.documenten}*/}
            {/*    chunkSize={200000}*/}
            {/*    minFileSize={1}*/}
            {/*    maxFileSize={40000000}*/}
            {/*    multiple*/}
            {/*    //onUploaded={onDocUploadedHandler}*/}
            {/*/>*/}
        </div>

        <div className="form-group">
            <label>Beschrijving</label>
            <HtmlEditor
                id={"gp_omschr_" + data.id}
            
                allowSoftLineBreak
                mediaResizing={htmlOptions.mediaResizing}
                tableResizing={htmlOptions.tableResizing}
                tableContextMenu={htmlOptions.tableContextMenu}
                valueType="html"
                value={data.htmlOmschrijving}
                onValueChanged={omschrChangedHandler}
            >
                <Toolbar multiline>
                    <Item name="undo" />
                    <Item name="redo" />
                    <Item name="separator" />
                    <Item
                        name="size"
                        acceptedValues={htmlOptions.sizeValues}
                    />
                    <Item
                        name="font"
                        acceptedValues={htmlOptions.fontValues}
                    />
                    <Item name="separator" />
                    <Item name="bold" />
                    <Item name="italic" />
                    <Item name="strike" />
                    <Item name="underline" />
                    <Item name="separator" />
                    <Item name="alignLeft" />
                    <Item name="alignCenter" />
                    <Item name="alignRight" />
                    <Item name="alignJustify" />
                    <Item name="separator" />
                    <Item name="orderedList" />
                    <Item name="bulletList" />
                    <Item name="separator" />
                    <Item
                        name="header"
                        acceptedValues={htmlOptions.headerValues}
                    />
                    <Item name="separator" />
                    <Item name="color" />
                    <Item name="background" />
                    <Item name="separator" />
                    <Item name="link" />
                    <Item name="image" />
                    <Item name="separator" />
                    <Item name="clear" />
                    <Item name="codeBlock" />
                    <Item name="blockquote" />
                    <Item name="separator" />
                    <Item name="insertTable" />
                    <Item name="deleteTable" />
                    <Item name="insertRowAbove" />
                    <Item name="insertRowBelow" />
                    <Item name="deleteRow" />
                    <Item name="insertColumnLeft" />
                    <Item name="insertColumnRight" />
                    <Item name="deleteColumn" />
                </Toolbar>

                <Validator>
                    <RequiredRule message="Beschrijving is verplicht." />
                </Validator>
            
            </HtmlEditor>
        </div>

        <div className="form-group">
            <ValidationSummary width="100%" />
        </div>

        <div className="form-group buttons">
            <Button text="Opslaan als concept" onClick={onSaveHandler} type="normal" visible={!data.gepubliceerd} />
            <Button text="Publiceren" onClick={onPublishHandler} type="default" />
        </div>

        
    </ValidationGroup>;
}
export default GoedePraktijk;